import { useState, useEffect } from 'react';
import { AlertTriangle, Eye, X, Flag, User, Calendar, ImageIcon, Loader2, ChevronLeft, ChevronRight, Shield, CheckCircle } from 'lucide-react';
import { galleryAPI, type GalleryWork, type PaginationInfo } from '../../services/api';
import toast from '../Toast';
import { formatBeijingDateTime } from '../../utils/time';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

interface ReportedWork extends GalleryWork {
  reportCount: number;
}

interface ReportedWorksTabProps {
  onRefresh?: () => void;
}

export default function ReportedWorksTab({ onRefresh }: ReportedWorksTabProps) {
  const [works, setWorks] = useState<ReportedWork[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [previewWork, setPreviewWork] = useState<ReportedWork | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const loadReportedWorks = async (page = 1) => {
    setLoading(true);
    try {
      const res = await galleryAPI.getReportedWorks({ page, limit: 20 });
      setWorks(res.data as ReportedWork[]);
      setPagination(res.pagination);
    } catch (error) {
      toast.error('加载举报列表失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadReportedWorks();
  }, []);

  const handleUnpublish = async (id: string) => {
    if (!confirm('确定要下架这个作品吗？')) return;
    setProcessingId(id);
    try {
      await galleryAPI.unpublishWork(id);
      toast.success('作品已下架');
      loadReportedWorks(pagination.page);
      onRefresh?.();
    } catch (error) {
      toast.error('下架失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
    setProcessingId(null);
  };

  const handleClearReports = async (id: string) => {
    if (!confirm('确定要清空这个作品的举报次数吗？')) return;
    setProcessingId(id);
    try {
      await galleryAPI.clearWorkReports(id);
      toast.success('举报已清空');
      loadReportedWorks(pagination.page);
      onRefresh?.();
    } catch (error) {
      toast.error('清空举报失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
    setProcessingId(null);
  };

  const handlePageChange = (page: number) => {
    loadReportedWorks(page);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatBeijingDateTime(dateString);
  };

  if (loading && works.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-light-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Flag className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-light-muted">被举报作品</p>
              <p className="text-xl font-bold text-light-text">{pagination.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-light-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-light-muted">总举报次数</p>
              <p className="text-xl font-bold text-light-text">
                {works.reduce((sum, w) => sum + (w.reportCount || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-light-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-light-muted">当前页</p>
              <p className="text-xl font-bold text-light-text">
                {pagination.page} / {pagination.totalPages}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Works List */}
      {works.length === 0 ? (
        <div className="bg-white rounded-xl border border-light-border p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-light-muted">暂无被举报的作品</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-light-border overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-light-hover">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-muted">作品</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-muted">作者</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-muted">举报次数</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-muted">发布时间</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-light-muted">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border">
                {works.map((work) => (
                  <tr key={work.id} className="hover:bg-light-hover/50">
                    <td className="px-4 py-3">
                      <WorkSummary work={work} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-light-muted" />
                        <span className="text-sm text-light-text">{work.user.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ReportBadge count={work.reportCount} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-light-muted">
                        <Calendar className="w-4 h-4" />
                        {formatDate(work.publishedAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <WorkActions
                        work={work}
                        processingId={processingId}
                        onPreview={setPreviewWork}
                        onIgnore={handleClearReports}
                        onUnpublish={handleUnpublish}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {works.map((work) => (
              <div key={work.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <WorkSummary work={work} />
                  <ReportBadge count={work.reportCount} />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InfoTile label="作者" value={work.user.name || '-'} />
                  <InfoTile label="发布时间" value={formatDate(work.publishedAt)} />
                </div>
                <WorkActions
                  work={work}
                  processingId={processingId}
                  onPreview={setPreviewWork}
                  onIgnore={handleClearReports}
                  onUnpublish={handleUnpublish}
                  mobile
                />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-light-border">
              <p className="text-sm text-light-muted">
                共 {pagination.total} 条记录
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="p-2 rounded-lg border border-light-border hover:border-accent-blue disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-light-secondary px-3">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="p-2 rounded-lg border border-light-border hover:border-accent-blue disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewWork && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-light-border">
              <div>
                <h3 className="text-lg font-semibold text-light-text">作品详情</h3>
                <p className="text-sm text-light-muted mt-1">举报次数: {previewWork.reportCount}</p>
              </div>
              <button
                onClick={() => setPreviewWork(null)}
                className="p-2 hover:bg-light-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-light-muted" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Image */}
                <div>
                  <div className="bg-light-hover rounded-xl overflow-hidden">
                    {previewWork.resultUrl ? (
                      <img
                        src={resolveBrowserMediaUrl(previewWork.resultUrl)}
                        alt={previewWork.publicTitle}
                        className="w-full h-auto object-contain"
                      />
                    ) : (
                      <div className="aspect-square flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-light-muted" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-light-text mb-1">{previewWork.publicTitle}</h4>
                    <p className="text-sm text-light-muted">{previewWork.publicDescription || '无描述'}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div className="p-3 bg-light-hover rounded-lg">
                      <div className="text-light-muted text-xs mb-1">作者</div>
                      <div className="font-medium">{previewWork.user.name}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-lg">
                      <div className="text-light-muted text-xs mb-1">模型</div>
                      <div className="font-medium">{previewWork.model}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-lg">
                      <div className="text-light-muted text-xs mb-1">尺寸</div>
                      <div className="font-medium">{previewWork.size}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-lg">
                      <div className="text-light-muted text-xs mb-1">质量</div>
                      <div className="font-medium">{previewWork.quality}</div>
                    </div>
                  </div>

                  {previewWork.prompt && (
                    <div>
                      <h5 className="text-sm font-medium text-light-text mb-2">提示词</h5>
                      <div className="p-3 bg-light-hover rounded-lg text-sm text-light-text max-h-32 overflow-y-auto">
                        {previewWork.prompt}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                    <button
                      onClick={() => {
                        handleClearReports(previewWork.id);
                        setPreviewWork(null);
                      }}
                      disabled={processingId === previewWork.id}
                      className="flex-1 px-4 py-2.5 bg-green-50 text-green-600 rounded-xl font-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                    >
                      {processingId === previewWork.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        '忽略举报'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        handleUnpublish(previewWork.id);
                        setPreviewWork(null);
                      }}
                      disabled={processingId === previewWork.id}
                      className="flex-1 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      {processingId === previewWork.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        '下架作品'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkSummary({ work }: { work: ReportedWork }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-12 h-12 bg-light-hover rounded-lg overflow-hidden flex-shrink-0">
        {work.resultUrl ? (
          <img
            src={resolveBrowserMediaUrl(work.resultUrl)}
            alt={work.publicTitle}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-light-muted" />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-light-text truncate max-w-[200px]">
          {work.publicTitle}
        </p>
        <p className="text-xs text-light-muted truncate max-w-[200px]">
          {work.publicDescription || work.prompt || '无描述'}
        </p>
      </div>
    </div>
  );
}

function ReportBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-600 rounded-lg text-sm font-medium shrink-0">
      <Flag className="w-3 h-3" />
      {count}
    </span>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 text-sm text-light-text break-all">{value}</div>
    </div>
  );
}

function WorkActions({
  work,
  processingId,
  onPreview,
  onIgnore,
  onUnpublish,
  mobile = false,
}: {
  work: ReportedWork;
  processingId: string | null;
  onPreview: (work: ReportedWork) => void;
  onIgnore: (id: string) => void;
  onUnpublish: (id: string) => void;
  mobile?: boolean;
}) {
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'flex items-center justify-end gap-2'}>
      <button
        onClick={() => onPreview(work)}
        className={mobile
          ? 'inline-flex items-center gap-1 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
          : 'p-2 text-light-muted hover:text-accent-blue hover:bg-accent-blue/10 rounded-lg transition-colors'}
        title="查看详情"
      >
        <Eye className="w-4 h-4" />
        {mobile && '详情'}
      </button>
      <button
        onClick={() => onIgnore(work.id)}
        disabled={processingId === work.id}
        className={mobile
          ? 'inline-flex items-center justify-center rounded-lg border border-green-200 px-3 py-2 text-xs text-green-600 hover:bg-green-50 disabled:opacity-50'
          : 'px-3 py-1.5 text-sm text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50'}
      >
        {processingId === work.id ? <Loader2 className="w-4 h-4 animate-spin" /> : mobile ? '忽略举报' : '忽略'}
      </button>
      <button
        onClick={() => onUnpublish(work.id)}
        disabled={processingId === work.id}
        className={mobile
          ? 'inline-flex items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50'
          : 'px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50'}
      >
        {processingId === work.id ? <Loader2 className="w-4 h-4 animate-spin" /> : mobile ? '下架作品' : '下架'}
      </button>
    </div>
  );
}
